<template>
  <nav
    :class="[
      'owc-subnavigation',
      {
        'owc-subnavigation--editMode': isEditMode,
      },
    ]"
    ref="subnavigation"
    :aria-label="subnavigationAriaLabel"
    :data-theme="brand"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div
      :class="[
        'owc-subnavigation__inner',
        {
          'owc-subnavigation__inner--sticky': isStickingOnTop,
          'owc-subnavigation__inner--show-topbar': isSubnavigationTopbarVisible,
          'owc-subnavigation__inner--editMode': isEditMode,
        },
      ]"
    >
      <div
        :class="[
          'owc-subnavigation__car-model-and-cta-container',
          { 'owc-subnavigation__shadow': !isArrowMenuVisible },
        ]"
      >
        <div class="wb-grid-container">
          <div class="wb-grid-row">
            <div class="wb-grid-col-mq1-8 wb-grid-col-mq3-6">
              <div class="owc-subnavigation__toggle-button-and-label-wrapper wb-grid-row">
                <div
                  v-if="!isMobile && navigationItems.length"
                  class="owc-subnavigation__toggle-arrow-menu-button wb-grid-col-mq1-1 wb-grid-col-mq3-1"
                >
                  <wb-icon
                    :name="isArrowMenuVisible ? 'bds/close/24' : 'bds/menu/24'"
                    aria-hidden="true"
                    @click="toggleArrowMenu()"
                  ></wb-icon>
                </div>
                <div class="owc-subnavigation__car-model-container wb-grid-col-mq1-11 wb-grid-col-mq3-11">
                  <h2 class="owc-subnavigation__car-model">{{ displayLabel }}</h2>
                  <span
                    v-if="isMobile && activeNavigationItem"
                    class="owc-subnavigation__car-model-navigation-item"
                  >
                    {{ activeNavigationItem.label }}
                  </span>
                </div>
              </div>
            </div>
            <div
              class="owc-subnavigation__cta-container wb-grid-col-mq1-4 wb-grid-col-mq3-6"
              @transitionend="transitionEnded"
            >
              <SlotContainer
                v-if="shouldShowToggleB2BB2C && !isMobile && isTopbarVisible"
                class="owc-subnavigation__item-B2BB2C-toggle"
                refName="slot-seamless-emh-sticky-content"
                slotName="seamless-emh-sticky-content"
              >
                <slot slot="seamless-emh-sticky-content" name="seamless-emh-sticky-content"></slot>
              </SlotContainer>
              <SubnavigationCtaButtons
                v-if="!isMobile"
                :ctaConfig="personaliseableData.cta"
                :buttons="personaliseableData.ctaItems"
                :mobile="isMobile"
                :vehicleData="personaliseableData.vehicleData"
              />
              <div
                v-if="isMobile"
                :class="[
                  'owc-subnavigation__sliding-cta-buttons',
                  {
                    'owc-subnavigation__sliding-cta-buttons--both-visible': isConfigurationButtonVisible,
                    'owc-subnavigation__sliding-cta-buttons--transitioned': isCtaButtonsTransitioned,
                  },
                ]"
              >
                <SubnavigationArrowButton
                  aria-label="Open Subnavigation"
                  iconName="bds/chevron-down/24"
                  :hasBackground="false"
                  :buttonOptionalClasses="['owc-subnavigation__arrow-button']"
                  @click.native="openSecondLevel"
                />
                <!-- stop delegation of the transitioned event to avoid filtering of the parents transition event -->
                <template v-if="isAutomatedDisplayMethod">
                  <SubnavigationCtaButtons
                    v-if="isMobile"
                    :ctaConfig="personaliseableData.cta"
                    :buttons="personaliseableData.ctaItems"
                    :mobile="isMobile"
                    :isIconOnly="true"
                    :isConfigurationButtonVisible="isConfigurationButtonVisible"
                    :isCtaButtonsTransitioned="isCtaButtonsTransitioned"
                    :vehicleData="personaliseableData.vehicleData"
                    :class="[
                      'owc-subnavigation__cta-button-primary',
                      {
                        'owc-subnavigation__cta-button-primary--visible': isConfigurationButtonVisible,
                        'owc-subnavigation__cta-button-primary--active': isCtaButtonsTransitioned,
                      },
                    ]"
                  />
                </template>
                <template v-else>
                  <a
                    v-if="ctaPrimary"
                    :href="ctaPrimary.link.url"
                    :target="ctaPrimary.link.target"
                    :aria-label="ctaPrimary.link.label"
                    :class="[
                      'wb-button',
                      'wb-button--primary',
                      'wb-button--medium',
                      'wb-button--icon-only',
                      'owc-subnavigation__cta-button-primary',
                      {
                        'owc-subnavigation__cta-button-primary--visible': isConfigurationButtonVisible,
                        'owc-subnavigation__cta-button-primary--active': isCtaButtonsTransitioned,
                      },
                    ]"
                    @transitionend.stop=""
                  >
                    <wb-icon class="wb-button__icon" aria-hidden="true" :name="ctaPrimary.icon"></wb-icon>
                  </a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SubnavigationArrows
        :class="[
          'owc-subnavigation__arrows',
          { 'owc-subnavigation__arrows-hide': isSubnavigationTopbarVisible && !isArrowMenuVisible },
        ]"
        v-if="!isMobile"
        :isMobile="isMobile"
        :navigationItems="navigationItems"
        :displayLabel="displayLabel"
        :shouldShowToggleB2BB2C="shouldShowToggleB2BB2C"
        :isTopbarVisible="isTopbarVisible"
      />
    </div>
    <transition
      name="second-level-transition"
      @after-enter="lockWindowScroll"
      @before-leave="unlockWindowScroll"
      appear
    >
      <div
        v-if="isMobile && isSecondLevelOpen"
        class="owc-subnavigation__second-level—container"
        ref="secondLevelContainer"
        @scroll="handleSecondLevelScroll"
      >
        <div
          :class="[
            'owc-subnavigation__second-level-navigation-top-container',
            {
              'owc-subnavigation__second-level-navigation-top-container--scrolled': isSecondLevelContainerScrolled,
            },
          ]"
        >
          <div class="wb-grid-container">
            <div class="owc-subnavigation__second-level-car-model-container wb-grid-row">
              <h2 class="owc-subnavigation__second-level-car-model wb-grid-col-mq1-10">
                {{ displayLabel }}
              </h2>
              <div class="owc-subnavigation__second-level-close-button-container wb-grid-col-mq1-2">
                <!-- TODO: aria label for close Second level (still aligning with BE) -->
                <button
                  v-if="isMobile"
                  type="button"
                  aria-label="close Second level"
                  class="owc-subnavigation__second-level-close-button wb-round-button wb-round-button--medium"
                  @click="closeSecondLevel"
                >
                  <wb-icon class="wb-round-button__icon" aria-hidden="true" name="bds/close/24"></wb-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="wb-grid-container owc-subnavigation__second-level-navigation-bottom-container">
          <div class="wb-grid-row">
            <div class="owc-subnavigation__second-level-navigation-items-container wb-grid-col-mq1-12">
              <a
                v-for="(navigationItem, index) in navigationItems"
                :key="index"
                :href="navigationItem.url"
                :target="navigationItem.target"
                :class="[
                  'owc-subnavigation__second-level-navigation-item',
                  {
                    'owc-subnavigation__second-level-navigation-item--active': navigationItem.isActive,
                  },
                ]"
              >
                {{ navigationItem.label }}
              </a>
              <SlotContainer
                v-if="shouldShowToggleB2BB2C"
                class="owc-subnavigation__item-B2BB2C-toggle"
                refName="slot-seamless-emh-sticky-content"
                slotName="seamless-emh-sticky-content"
              >
                <slot slot="seamless-emh-sticky-content" name="seamless-emh-sticky-content"></slot>
              </SlotContainer>
              <div class="owc-subnavigation__second-level-ctas">
                <SubnavigationCtaButtons
                  v-if="isMobile"
                  :ctaConfig="personaliseableData.cta"
                  :buttons="personaliseableData.ctaItems"
                  :mobile="isMobile"
                  :vehicleData="personaliseableData.vehicleData"
                  @closeSecondLevel="closeSecondLevel"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script lang="ts">
/*
 * `Subnavigation` - The subnavigation is used to help the users access lower-level categories in the site&#39;s information architecture.
 *  Subnavigation should only be used whenever we have a lot of information in a certain section/page
 *  of the website that needs to be organised in smaller sections. .
 */
import { SubnavigationComponent, SubnavigationCTA } from './Subnavigation';
import { unlockWindowScroll, lockWindowScroll } from '@owc-utils/common/LockWindow';

import SubnavigationArrowButton from './SubnavigationArrowButton.vue';
import SubnavigationArrows from './SubnavigationArrows.vue';
import SubnavigationCtaButtons from './SubnavigationCtaButtons.vue';
import { NavigationOption } from '../src/Subnavigation.enums';
import { MediaQueryService } from '@owc-utils/MediaQueryService';
import { MediaQuery } from '@owc-utils/MediaQueryService/MediaQuery';
import { Breakpoints, desktopBreakpoints } from '@owc-utils/MediaQueryService';
import {
  STAGE_TAG_NAME_LIST,
  SUBNAVIGATION_DEBOUNCE_TIME,
  SUBNAVIGATION_SCROLL_THROTTLE_LIMIT,
  SUBNAVIGATION_STAGE_COVERAGE_BEFORE_LEAVE,
  MOBILE_BREAKPOINTS_SUBNAVIGATION,
} from './Subnavigation.config';
import { ButtonType } from '@owc-types/Button.enum';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import Personalisation from '@owc-utils/Mixins/Personalisation';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { CtaButtonsDisplayMethod } from '@owc-utils/AutomatedCta/CtaAutomation.enum';
import { setBrandTheme } from '@owc-utils/Theming/theming';

let waitForStage: ReturnType<typeof setInterval> | null = null;

export default (ComponentInitializeDispatcher as SubnavigationComponent).extend({
  name: 'owc-subnavigation',
  components: {
    SubnavigationArrowButton,
    SubnavigationArrows,
    SubnavigationCtaButtons,
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [
    createEventListener('scroll', 'scrollHandler', { passive: true }),
    createEventListener('resize', 'resizeEventHandler', {
      debounceTime: SUBNAVIGATION_DEBOUNCE_TIME,
    }),
    OwcDataComponent,
    Personalisation,
  ],
  data() {
    return {
      isSecondLevelContainerScrolled: false,
      isMobile: false,
      isDesktop: false,
      isMQ3: false,
      isSecondLevelOpen: false,
      mediaQueryService: new MediaQueryService(window),
      isConfigurationButtonVisible: false,
      isTopbarVisible: false,
      isStickingOnTop: false,
      isCtaButtonsTransitioned: false,
      subnavigationOffsetTop: 0,
      scrollHandler: null,
      resizeHandler: null,
      stageHeight: 0,
      isArrowMenuVisible: false,
    };
  },
  computed: {
    isSubnavigationTopbarVisible: function() {
      return this.isMobile || this.isTopbarVisible;
    },
    displayLabel: function() {
      return this.personaliseableData?.displayLabel || '';
    },
    visibilityOption() {
      return this.componentData?.visibilitySettings;
    },
    shouldShowToggleB2BB2C() {
      return Boolean(this.visibilityOption?.toggleB2BB2CEnabled);
    },
    navigationItems: function() {
      const navigationItems = this.personaliseableData?.navigationItems;

      if (!navigationItems) {
        return [];
      }

      if (this.isMobile) {
        return navigationItems.filter(
          item =>
            item.navOption == NavigationOption.showAlways || item.navOption == NavigationOption.hideOnDesktop,
        );
      } else {
        return navigationItems.filter(
          item =>
            item.navOption == NavigationOption.showAlways || item.navOption == NavigationOption.hideOnMobile,
        );
      }
    },
    activeNavigationItem: function() {
      return this.navigationItems.find(navigationItem => navigationItem.isActive);
    },
    ctaItems: function() {
      return this.personaliseableData?.ctaItems || [];
    },
    ctaPrimary: function() {
      return this.getItemFromType(this.ctaItems, ButtonType.Primary);
    },
    subnavigationAriaLabel: function() {
      return this.personaliseableData?.description || '';
    },
    isAutomatedDisplayMethod: function() {
      return this.personaliseableData.cta?.ctaDisplayMethod === CtaButtonsDisplayMethod.Automate;
    },
    brand() {
      return setBrandTheme();
    },
  },
  created() {
    this.scrollHandler = this.createDynamicallyThrottledListener();
  },
  mounted() {
    window.requestAnimationFrame(() => {
      this.resizeEventHandler();
      this.mediaQueryService.observe(true);

      this.scrollEventHandler();
    });
  },
  methods: {
    transitionEnded() {
      this.isCtaButtonsTransitioned = this.isConfigurationButtonVisible;
    },
    updateDeviceViewportFlags: function(mq: MediaQuery) {
      this.isMobile = MOBILE_BREAKPOINTS_SUBNAVIGATION.includes(mq);
      this.isDesktop = desktopBreakpoints.includes(mq);
      this.isMQ3 = Breakpoints.mq3 === mq;
    },
    resizeEventHandler() {
      const currentMediaQuery = this.mediaQueryService.getCurrentMediaQuery();
      this.updateDeviceViewportFlags(currentMediaQuery);

      const stageElement = this.queryStageElement();

      if (stageElement) {
        this.stageHeight = stageElement.clientHeight;
        /**
         * If the stage element exists but the height is still zero, this means
         * that the stage has not yet loaded and we are in for a ride on the
         * CLS train. Unfortunately we'll need to wait for it to be fully loaded
         * so that the subnavigation 'button shortcut behavior' works correctly.
         *
         * If after 10 seconds the stage hasn't been shown yet we just give up, something is clearly
         * wrong or internet is really slow, and a shortcut feature should not be taking up
         * useful resources
         */
        if (this.stageHeight === 0 && !waitForStage) {
          // Max iteration time of 10 seconds
          let waitForStageIterations = 20;

          waitForStage = setInterval(() => {
            this.stageHeight = stageElement.clientHeight;

            if (waitForStage && this.stageHeight !== 0 && waitForStageIterations > 0) {
              clearInterval(waitForStage);
            } else {
              --waitForStageIterations;
            }
          }, 500);
        }
      }
    },
    /*
     * To prevent the subnavigation from jumping when the user scrolls
     * the idea is to have a region with lower throttling limit.
     */
    createDynamicallyThrottledListener() {
      let dynamicThottleLimit = 1;
      let ableToExecute = true;
      return () => {
        if (!ableToExecute) {
          return;
        }
        this.scrollEventHandler();

        // To ensure that the subnavigation is placed in the middle
        // of that super sensible region the top offset to multiplied by 2
        dynamicThottleLimit =
          this.subnavigationOffsetTop * 2 < window.scrollY ? SUBNAVIGATION_SCROLL_THROTTLE_LIMIT : 1;
        // reset the execution flag and set timeout with new throttle limit
        ableToExecute = false;
        setTimeout(() => (ableToExecute = true), dynamicThottleLimit);
      };
    },
    scrollEventHandler() {
      // Moved the check into the scroll function to prevent the
      // subnavigation jumping into the header. When header pops in later then the subnavigation
      // and the offsetTop is not updated then the subnaavigation jumps right into
      // the header.
      const offsetTop = (this.$el as HTMLElement).offsetTop;
      if (offsetTop > 0 && this.subnavigationOffsetTop !== offsetTop) {
        this.subnavigationOffsetTop = offsetTop;
      }

      const scrollY = window.scrollY;
      this.isStickingOnTop = scrollY > 0 && scrollY >= this.subnavigationOffsetTop;

      if (this.stageHeight > 0) {
        const visibleStageHeight = this.stageHeight * SUBNAVIGATION_STAGE_COVERAGE_BEFORE_LEAVE;
        if (scrollY > visibleStageHeight) {
          this.leavingStage();
        } else {
          this.enteringStage();
        }
      }
    },
    queryStageElement() {
      const stageElements = STAGE_TAG_NAME_LIST.join(',');

      return document.querySelector(`${stageElements}`);
    },
    toggleArrowMenu() {
      this.isArrowMenuVisible = !this.isArrowMenuVisible;
    },
    leavingStage() {
      this.isTopbarVisible = true;
      this.isConfigurationButtonVisible = true;
    },
    enteringStage() {
      this.isTopbarVisible = false;
      this.isConfigurationButtonVisible = false;
    },
    getItemFromType(itemArray: SubnavigationCTA[], type: ButtonType) {
      return itemArray.find(item => item.link.type === type);
    },
    openSecondLevel: function() {
      this.isSecondLevelOpen = true;
    },
    closeSecondLevel: function() {
      this.isSecondLevelOpen = false;
    },
    handleSecondLevelScroll: function() {
      const secondLevelContainer = this.$refs.secondLevelContainer as HTMLElement;
      const amountScrolled = secondLevelContainer.scrollTop;
      this.isSecondLevelContainerScrolled = amountScrolled > 0;
    },
    unlockWindowScroll,
    lockWindowScroll,
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';

.owc-subnavigation {
  $subnavigation-row-height: 56px;
  $topbar-transition-duration: 300ms;
  $cta-primary-mobile-translateX: 8px;
  $sticky-subnaviation-z-index: $z-index-overlay - 1;

  height: $subnavigation-row-height;
  overflow: hidden;

  &--editMode {
    height: auto;
  }
  &__shadow {
    box-shadow: var(--wb-shadow-level-2);
  }
  &__inner {
    transform: translateY(-$subnavigation-row-height);

    transition: transform $topbar-transition-duration var(--wb-fade);

    &--show-topbar {
      transform: translateY(0);
    }
    &--sticky {
      position: fixed;
      top: 0;

      z-index: $sticky-subnaviation-z-index;

      width: 100%;
      height: auto;
    }
    &--editMode {
      transform: none;
    }
  }
  &__car-model-and-cta-container {
    display: flex;
    align-items: center;
    height: $subnavigation-row-height;
    min-height: $subnavigation-row-height;
    padding-top: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--wb-grey-85);

    background: var(--wb-white);
  }
  .owc-subnavigation__toggle-button-and-label-wrapper {
    height: 100%;

    .owc-subnavigation__toggle-arrow-menu-button {
      display: flex;
      flex-flow: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      margin: auto;
      padding: 0;

      border-right: 1px solid var(--wb-grey-85);

      color: var(--wb-grey-20);

      cursor: pointer;
    }
  }
  &__arrows {
    z-index: $z-index-negative;

    max-height: $subnavigation-row-height;

    box-shadow: var(--wb-shadow-level-2);

    transition: max-height $topbar-transition-duration var(--wb-fade);

    &-hide {
      max-height: 0;
    }
  }
  &__car-model-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
  &__car-model {
    width: 100%;
  }
  &__car-model,
  &__second-level-car-model {
    @include wb-type-copy-strong;
    overflow: hidden;

    color: var(--wb-grey-20);

    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__car-model-navigation-item {
    @include wb-type-copy-secondary;
    width: 100%;
    overflow: hidden;

    color: var(--wb-grey-45);

    white-space: nowrap;
    text-overflow: ellipsis;
  }
  &__cta-container {
    display: flex;

    align-items: center;
    justify-content: flex-end;
  }
  &__secondary-cta {
    margin: 0 var(--wb-spacing-xxs);
  }
  &__sliding-cta-buttons {
    display: flex;
    align-items: center;

    @include breakpoint-to(mq4) {
      transform: translateX(50%);

      transition: transform 0.6s var(--wb-move);

      &--both-visible {
        transform: translateX(0);
      }
      &--transitioned {
        transition-delay: 0.2s;
      }
    }
  }
  &__cta-button-primary {
    transform: translateX($cta-primary-mobile-translateX);
    opacity: 0;

    transition: opacity 0.3s var(--wb-fade) 0.1s, transform 0.4s linear 0.2s;

    pointer-events: none;

    &--visible {
      transform: translateX(0);
      opacity: 1;

      pointer-events: all;
    }
    &--active {
      // resting delay by `transition-delay: 0;` seems not to work
      // so just override the transition without the delay
      transition: opacity 0.3s var(--wb-fade), transform 0.4s linear;
    }
  }
  &__second-level—container {
    position: fixed;
    top: 0;
    z-index: $z-index-overlay;

    width: 100vw;
    height: 100vh;
    overflow: scroll;

    background-color: var(--wb-white);
  }
  &__second-level-navigation-top-container {
    position: sticky;
    top: 0;

    z-index: $z-index-overlay;

    background-color: var(--wb-white);

    &--scrolled {
      box-shadow: var(--wb-shadow-level-2);
    }
  }
  &__second-level-car-model-container {
    align-items: center;
    min-height: $subnavigation-row-height;
  }
  &__second-level-close-button-container {
    display: flex;
    justify-content: flex-end;
  }
  &__second-level-navigation-items-container {
    padding-top: 32px;
    padding-bottom: $subnavigation-row-height;
  }
  &__second-level-navigation-item {
    @include wb-type-copy;
    display: flex;
    align-items: center;

    padding: 16px var(--wb-spacing-xs);

    color: var(--wb-grey-45);
    white-space: nowrap;

    text-decoration: none;

    &--active {
      @include wb-type-copy-strong;
      color: var(--wb-grey-20);

      background: var(--wb-grey-85);

      pointer-events: none;
    }
    &:not(:last-of-type) {
      border-bottom: 1px solid var(--wb-grey-85);
    }
  }
  &__item-B2BB2C-toggle {
    margin: var(--wb-spacing-m) 0;

    @include breakpoint-from(mq5) {
      height: 100%;
      margin: 0;

      & + a {
        margin-left: var(--wb-spacing-s);
      }
    }
  }
}
.second-level-transition {
  &-enter-active,
  &-leave-active {
    transition: transform 500ms ease-in-out;
  }
  &-enter,
  &-leave-to {
    transform: translateY(-100%);
  }
  &-leave,
  &-enter-to {
    transform: translateY(0);
  }
}
</style>
